import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { formatProfessionalFullName } from '@/utils';
import {
	Check,
	FileText,
	Info,
	MapPin,
	MoreVertical,
	PencilLine,
	Users,
	Video,
	X,
	BarChart3,
} from 'lucide-react';
import { ColumnDef } from '@/components';
import { AnchoredDropdownMenu } from '@/components/ui/AnchoredDropdownMenu';
import { Tooltip } from '@/components/ui/Tooltip';
import { SERVICE_MODALITY_TRANSLATIONS } from '@/constants/professional/translations';
import { ProfessionalVisibilityIcon } from './ProfessionalVisibilityIcon';
import {
	professionalHasAISandboxPermission,
	professionalHasB2CPermission,
	professionalHasP2PPermission,
	professionalHasTranscriptionForDefaultSessionsPermission,
	professionalHasTranscriptionMockPermission,
	professionalHasTranscriptionPermission,
} from '@/utils/professionalPermissionTags';

interface ProfessionalColumnsProps {
	onEdit: (id: string) => void;
	onEditPatients?: (id: string) => void;
	onViewUsage?: (id: string) => void;
	onViewAudit?: (professional: Professionals.Professional) => void;
}

const ICON_COLUMN_HEADER_CLASS =
	'w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] px-1.5 text-center whitespace-normal leading-[1.15] align-middle';
const ICON_COLUMN_CELL_CLASS =
	'w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] px-1.5 text-center cursor-default';

const PROFESSIONAL_COLUMN_HEADER_CLASS =
	'w-[9.5rem] min-w-[9.5rem] max-w-[9.5rem] px-2';
const PROFESSIONAL_COLUMN_CELL_CLASS =
	'w-[9.5rem] min-w-[9.5rem] max-w-[9.5rem] px-2 cursor-text';

const SERVICE_COLUMN_HEADER_CLASS =
	'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2';
const SERVICE_COLUMN_CELL_CLASS =
	'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2 cursor-text';

const IconColumnCell = ({ children }: { children: ReactNode }) => (
	<div className="flex w-full items-center justify-center">{children}</div>
);

const resolveProfessionalInitial = (row: Professionals.Professional) => {
	const source =
		row.first_name?.trim() ||
		row.last_name?.trim() ||
		row.email?.trim()?.charAt(0) ||
		'?';
	return source.charAt(0).toUpperCase();
};

const ProfessionalAvatar = ({ row }: { row: Professionals.Professional }) => {
	const [failed, setFailed] = useState(false);
	const initial = resolveProfessionalInitial(row);
	const photoUrl = row.profile_photo_url?.trim();

	useEffect(() => {
		setFailed(false);
	}, [photoUrl]);

	if (photoUrl && !failed) {
		return (
			<img
				src={photoUrl}
				alt=""
				referrerPolicy="no-referrer"
				onError={() => setFailed(true)}
				className="mr-0 h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-zinc-200/80"
			/>
		);
	}

	return (
		<div
			className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-bold uppercase text-zinc-600 ring-1 ring-zinc-200/80"
			aria-hidden
		>
			{initial}
		</div>
	);
};

const CheckStatusBadge = ({
	enabled,
	label,
}: {
	enabled: boolean;
	label: string;
}) => (
	<span
		className={cn(
			'inline-flex size-7 cursor-help items-center justify-center rounded-full',
			enabled
				? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100'
				: 'bg-stone-100 text-stone-300 ring-1 ring-stone-200/80',
		)}
		aria-label={label}
		title={label}
	>
		{enabled ? (
			<Check className="size-3.5" strokeWidth={2.75} aria-hidden />
		) : (
			<X className="size-3.5" strokeWidth={2.75} aria-hidden />
		)}
	</span>
);

const ColumnHeaderWithHint = ({
	label,
	hint,
}: {
	label: string;
	hint: string;
}) => (
	<span className="inline-flex max-w-full flex-col items-center justify-center gap-0.5">
		<span className="inline-flex items-center justify-center gap-0.5">
			<span className="text-center">{label}</span>
			<Tooltip content={hint} position="top">
				<button
					type="button"
					className="inline-flex shrink-0 cursor-help rounded-full text-stone-400 transition-colors hover:text-stone-600"
					aria-label={hint}
				>
					<Info
						className="size-3 shrink-0"
						strokeWidth={2.25}
						aria-hidden
					/>
				</button>
			</Tooltip>
		</span>
	</span>
);

const ServiceModalityGlyph = ({
	modality,
}: {
	modality: Professionals.ServiceModality;
}) => {
	const label = SERVICE_MODALITY_TRANSLATIONS[modality];

	const icon =
		modality === 'FULL_REMOTE' ? (
			<Video className="size-3.5" strokeWidth={2.25} aria-hidden />
		) : modality === 'FULL_PRESENCIAL' ? (
			<MapPin className="size-3.5" strokeWidth={2.25} aria-hidden />
		) : (
			<span className="inline-flex items-center gap-px" aria-hidden>
				<Video className="size-3" strokeWidth={2.25} />
				<MapPin className="size-3" strokeWidth={2.25} />
			</span>
		);

	return (
		<Tooltip content={label} position="top">
			<span
				className="inline-flex size-7 cursor-help items-center justify-center rounded-lg bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200/80"
				aria-label={label}
			>
				{icon}
			</span>
		</Tooltip>
	);
};

const ProfessionalActionsDropdown = ({
	row,
	onEdit,
	onEditPatients,
	onViewUsage,
	onViewAudit,
}: {
	row: Professionals.Professional;
	onEdit: (id: string) => void;
	onEditPatients: (id: string) => void;
	onViewUsage: (id: string) => void;
	onViewAudit: (professional: Professionals.Professional) => void;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const closeMenu = () => setIsOpen(false);

	return (
		<AnchoredDropdownMenu
			open={isOpen}
			onOpenChange={setIsOpen}
			menuClassName="animate-in fade-in zoom-in-95 duration-100"
			trigger={({ ref, isOpen: menuOpen, onTriggerClick }) => (
				<button
					ref={ref}
					type="button"
					onClick={onTriggerClick}
					className={cn(
						'flex h-8 w-8 cursor-pointer items-center justify-center rounded-xl border border-solid outline-none transition focus:outline-none',
						menuOpen
							? 'border-zinc-500 bg-zinc-100 text-zinc-900'
							: 'border-stone-300 bg-white text-zinc-600 hover:border-zinc-500',
					)}
					aria-label="Abrir menu de ações"
					aria-expanded={menuOpen}
					aria-haspopup="menu"
				>
					<MoreVertical size={18} strokeWidth={3} />
				</button>
			)}
		>
			<div className="flex flex-col py-2">
				<button
					type="button"
					onClick={() => {
						closeMenu();
						onEdit(row.id);
					}}
					className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
				>
					<PencilLine size={18} className="shrink-0 text-stone-700" />
					Editar perfil
				</button>
				<button
					type="button"
					onClick={() => {
						closeMenu();
						onEditPatients(row.id);
					}}
					className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
				>
					<Users size={18} className="shrink-0 text-stone-700" />
					Ver pacientes
				</button>
				<button
					type="button"
					onClick={() => {
						closeMenu();
						onViewUsage(row.id);
					}}
					className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
				>
					<BarChart3 size={18} className="shrink-0 text-stone-700" />
					Uso mensal
				</button>
				<button
					type="button"
					onClick={() => {
						closeMenu();
						onViewAudit(row);
					}}
					className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-stone-700 outline-none transition hover:bg-stone-100"
				>
					<FileText size={18} className="shrink-0 text-stone-700" />
					Auditoria
				</button>
			</div>
		</AnchoredDropdownMenu>
	);
};

const ProfessionalColumns = ({
	onEdit,
	onEditPatients = () => {},
	onViewUsage = () => {},
	onViewAudit = () => {},
}: ProfessionalColumnsProps): ColumnDef<Professionals.Professional, any>[] => {
	return [
		{
			header: (
				<ColumnHeaderWithHint
					label="Autoriz."
					hint="Profissional autorizado na plataforma"
				/>
			),
			accessor: 'authorized',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={value === true}
						label={value === true ? 'Autorizado' : 'Não autorizado'}
					/>
				</IconColumnCell>
			),
		},
		{
			header: 'Profissional',
			accessor: 'first_name',
			headerClassName: PROFESSIONAL_COLUMN_HEADER_CLASS,
			cellClassName: PROFESSIONAL_COLUMN_CELL_CLASS,
			cell: (_, row) => {
				const fullName = formatProfessionalFullName(row);
				const email = row.email || 'N/A';
				return (
					<div className="flex min-w-0 items-center gap-2">
						<ProfessionalAvatar row={row} />
						<div className="min-w-0 truncate">
							<p
								className="truncate text-xs font-medium text-dark"
								title={fullName}
							>
								{fullName}
							</p>
							<p
								className="truncate text-[11px] text-gray-500"
								title={email}
							>
								{email}
							</p>
						</div>
					</div>
				);
			},
		},
		{
			header: 'Serviço',
			accessor: 'professional_services',
			headerClassName: SERVICE_COLUMN_HEADER_CLASS,
			cellClassName: SERVICE_COLUMN_CELL_CLASS,
			cell: (value) => {
				const list = Array.isArray(value) ? value : [];
				const name = list[0]?.service?.service_name?.trim();
				return (
					<span
						className={cn(
							'block truncate text-xs',
							name ? 'text-dark' : 'italic text-gray-500',
						)}
						title={name || 'Sem serviço'}
					>
						{name || 'Sem serviço'}
					</span>
				);
			},
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Visib."
					hint="Visibilidade do profissional nas páginas de agendamento"
				/>
			),
			accessor: 'visibility',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<ProfessionalVisibilityIcon
						visibility={
							value as Professionals.Visibility | undefined
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Modal."
					hint="Modalidade de atendimento"
				/>
			),
			accessor: 'service_modality',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<ServiceModalityGlyph
						modality={value as Professionals.ServiceModality}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="P2P"
					hint="Profissional para pacientes"
				/>
			),
			accessor: 'permission_tags',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={professionalHasP2PPermission(row)}
						label={
							professionalHasP2PPermission(row)
								? 'P2P habilitado'
								: 'P2P não habilitado'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Sandbox"
					hint="Sandbox de IA (mock)"
				/>
			),
			accessor: 'permission_tags',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={professionalHasAISandboxPermission(row)}
						label={
							professionalHasAISandboxPermission(row)
								? 'Sandbox de IA habilitado'
								: 'Sandbox de IA não habilitado'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="B2C"
					hint="Gestão B2C — habilita profissional a gerenciar a agenda de horários dele"
				/>
			),
			accessor: 'permission_tags',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={professionalHasB2CPermission(row)}
						label={
							professionalHasB2CPermission(row)
								? 'Gestão B2C habilitada'
								: 'Gestão B2C não habilitada'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Transcr. P2P"
					hint="Transcrição em sessões P2P online"
				/>
			),
			accessor: 'permission_tags',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={professionalHasTranscriptionPermission(row)}
						label={
							professionalHasTranscriptionPermission(row)
								? 'Transcrição P2P habilitada'
								: 'Transcrição P2P não habilitada'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Transcr. B2C"
					hint="Transcrição em sessões DEFAULT online (agendamento B2C)"
				/>
			),
			accessor: 'permission_tags',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={professionalHasTranscriptionForDefaultSessionsPermission(
							row,
						)}
						label={
							professionalHasTranscriptionForDefaultSessionsPermission(
								row,
							)
								? 'Transcrição B2C habilitada'
								: 'Transcrição B2C não habilitada'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: (
				<ColumnHeaderWithHint
					label="Mock"
					hint="Transcrição simulada (uso interno dev/staging)"
				/>
			),
			accessor: 'permission_tags',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={professionalHasTranscriptionMockPermission(
							row,
						)}
						label={
							professionalHasTranscriptionMockPermission(row)
								? 'Transcrição mock habilitada'
								: 'Transcrição mock não habilitada'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: 'Google',
			accessor: 'google_permissions_allowed',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (value) => (
				<IconColumnCell>
					<CheckStatusBadge
						enabled={!!value}
						label={
							value
								? 'Google autorizado'
								: 'Google não autorizado'
						}
					/>
				</IconColumnCell>
			),
		},
		{
			header: 'Ações',
			accessor: 'id',
			headerClassName: ICON_COLUMN_HEADER_CLASS,
			cellClassName: ICON_COLUMN_CELL_CLASS,
			cell: (_, row) => (
				<IconColumnCell>
					<ProfessionalActionsDropdown
						row={row}
						onEdit={onEdit}
						onEditPatients={onEditPatients}
						onViewUsage={onViewUsage}
						onViewAudit={onViewAudit}
					/>
				</IconColumnCell>
			),
		},
	];
};

export { ProfessionalColumns };
